// Campaigns page: status tabs (Applied, In Progress, Completed, Archived) with
// Group by, grid / list views and the In Progress task filter
// (EPD-12315, EPD-12316, EPD-12317, EPD-12318, EPD-12319, EPD-5559, EPD-12321).

import { BrandLogosProvider } from "@/components/brand/brand-logos";
import { BrandCampaignsView } from "@/components/campaigns/brand-campaigns-view";
import {
  BrandGrid,
  BrandSummary,
  BrandTable,
} from "@/components/campaigns/brand-groups";
import { CampaignCard } from "@/components/campaigns/campaign-card";
import { CampaignTable } from "@/components/campaigns/campaign-table";
import {
  CampaignsEmpty,
  GroupByMenu,
  StatusTabs,
  TaskFilter,
  ViewToggle,
  type GroupBy,
  type CampaignView,
  type TaskOption,
} from "@/components/campaigns/campaigns-toolbar";
import { brandCoverImages, brandGroupsFor } from "@/lib/campaigns/brands";
import {
  parseCampaignsSearch,
  type CampaignsSearch,
} from "@/lib/campaigns/search";
import {
  buildCampaignItems,
  CAMPAIGN_STATUSES,
  CAMPAIGN_STATUS_LABELS,
  interactionKey,
  type CampaignItem,
  type CampaignStatus,
} from "@/lib/campaigns/status";
import { useReturnCampaignsWithNewTasks } from "@/hooks/use-return-campaigns";
import {
  type CreatorInteraction,
  projectInteractionsQueryOptions,
  useProjectApplicationsQuery,
  useProjectsListQuery,
} from "@/lib/queries/projects";
import { ensureOrgContext } from "@/lib/utils/org-context-guard";
import { Button, Card, CardContent, Skeleton } from "@repo/ui";
import { useQueries } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { FolderKanban, Search } from "lucide-react";
import { useMemo, useState } from "react";
import type { OrgRouteContext } from "../route";

const SKELETON_DELAYS = [0, 0.15, 0.3, 0.45, 0.6, 0.75] as const;

// Human-readable labels for each task type returned by the API
const TASK_TYPE_LABELS: Record<string, string> = {
  SIGN_BRIEF: "Sign Brief",
  SUBMIT_CONTENT: "Submit Content",
  PROMO_CODES: "Promo Codes",
  AFFILIATE_LINKS: "Affiliate Links",
  MEMBER_DATA_UPDATE: "Update Info",
  PRODUCT_SELECTION: "Select Products",
  CUSTOM_STAGE: "Custom Task",
  UNKNOWN: "Other",
};

export const Route = createFileRoute("/(org)/o/$orgSlug/projects/")({
  validateSearch: (search: Record<string, unknown>): CampaignsSearch =>
    parseCampaignsSearch(search),
  component: Projects,
});

const CAMPAIGN_GRID =
  "grid grid-cols-2 gap-3 md:grid-cols-[repeat(auto-fill,minmax(260px,1fr))] md:gap-5";

function Projects() {
  const { organization } = Route.useRouteContext() as OrgRouteContext;
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const organizationId = organization?.id ?? "";

  // Hooks must be called unconditionally. Pass a safe fallback so the query
  // is gated by enabled=false (see projectsListQueryOptions) when the parent
  // route's beforeLoad guarantee has broken — the ensureOrgContext branch
  // below handles the render path.
  const {
    data: projectsData,
    dataUpdatedAt: projectsUpdatedAt,
    isPending,
    error,
  } = useProjectsListQuery(organizationId);
  const { data: applicationsData, isPending: applicationsPending } =
    useProjectApplicationsQuery(organizationId);

  const projects = useMemo(() => projectsData?.projects ?? [], [projectsData]);
  const offerClients = useMemo(
    () => projectsData?.offerClients ?? [],
    [projectsData],
  );
  const applications = useMemo(
    () => applicationsData?.applications ?? [],
    [applicationsData],
  );

  // Every campaign's tasks decide its tab, the In Progress badge and the
  // task filter. TanStack Query deduplicates these across the page.
  const interactionQueries = useQueries({
    queries: projects.map((p) =>
      projectInteractionsQueryOptions(organizationId, p.id, p.profileId),
    ),
  });
  const anyInteractionsPending = interactionQueries.some((q) => q.isPending);

  const interactionMap = useMemo(() => {
    const map = new Map<string, CreatorInteraction[]>();
    projects.forEach((p, i) => {
      const data = interactionQueries[i]?.data;
      if (data) map.set(interactionKey(p), data.interactions ?? []);
    });
    return map;
  }, [projects, interactionQueries]);

  const items = useMemo(
    () => buildCampaignItems(projects, interactionMap, applications),
    [projects, interactionMap, applications],
  );

  useReturnCampaignsWithNewTasks(organizationId, items, projectsUpdatedAt);

  const itemsByStatus = useMemo(() => {
    const byStatus = Object.fromEntries(
      CAMPAIGN_STATUSES.map((status) => [status, [] as CampaignItem[]]),
    ) as Record<CampaignStatus, CampaignItem[]>;
    for (const item of items) byStatus[item.status].push(item);
    return byStatus;
  }, [items]);

  // Brand card images and logos come from every tab, so they stay put when
  // the tab changes (EPD-12612).
  const coverImages = useMemo(() => brandCoverImages(items), [items]);
  const logoClientIds = useMemo(
    () => [
      ...items.map((item) => item.project.client.id),
      ...offerClients.map((offerClient) => offerClient.id),
    ],
    [items, offerClients],
  );

  // Applied stays unknown (null, shown as "…") until its own query settles,
  // so the other tabs never wait on it.
  const counts = useMemo(
    () =>
      Object.fromEntries(
        CAMPAIGN_STATUSES.map((status) => [
          status,
          status === "applied" && applicationsPending
            ? null
            : itemsByStatus[status].length,
        ]),
      ) as Record<CampaignStatus, number | null>,
    [itemsByStatus, applicationsPending],
  );

  const tasks = useMemo(() => search.tasks ?? [], [search.tasks]);
  // Not in the URL, so every visit starts on Brand (EPD-12586).
  const [groupBy, setGroupBy] = useState<GroupBy>("brand");
  const view: CampaignView = search.view ?? "grid";

  // A brand link (breadcrumb, email) opens the first tab that has the brand.
  const requestedStatus: CampaignStatus = search.tab ?? "in-progress";
  const status: CampaignStatus = useMemo(() => {
    if (!search.brand) return requestedStatus;
    const order = [
      requestedStatus,
      ...CAMPAIGN_STATUSES.filter((s) => s !== requestedStatus),
    ];
    return (
      order.find((s) =>
        brandGroupsFor(s, itemsByStatus[s], offerClients).some(
          (group) => group.clientId === search.brand,
        ),
      ) ?? requestedStatus
    );
  }, [search.brand, requestedStatus, itemsByStatus, offerClients]);

  const isInProgress = status === "in-progress";
  const tabItems = itemsByStatus[status];
  const filterActive = isInProgress && tasks.length > 0;
  const visibleItems = filterActive
    ? tabItems.filter((item) =>
        item.actionTaskTypes.some((taskType) => tasks.includes(taskType)),
      )
    : tabItems;
  const groups = brandGroupsFor(
    status,
    visibleItems,
    filterActive ? [] : offerClients,
    coverImages,
  );

  const taskOptions: TaskOption[] = useMemo(() => {
    const countsByType = new Map<string, number>();
    for (const item of itemsByStatus["in-progress"]) {
      for (const taskType of new Set(item.actionTaskTypes)) {
        countsByType.set(taskType, (countsByType.get(taskType) ?? 0) + 1);
      }
    }
    return Array.from(countsByType, ([taskType, count]) => ({
      taskType,
      label: TASK_TYPE_LABELS[taskType] ?? taskType,
      count,
    })).sort((a, b) => a.label.localeCompare(b.label));
  }, [itemsByStatus]);

  const updateSearch = (patch: CampaignsSearch) =>
    navigate({
      to: ".",
      search: (prev) => parseCampaignsSearch({ ...prev, ...patch }),
    });

  if (!ensureOrgContext(organization, "projects/index.tsx#Projects")) {
    return null;
  }

  // Only the Applied tab needs the applications, so only it waits for them.
  if (
    isPending ||
    anyInteractionsPending ||
    (applicationsPending && status === "applied")
  ) {
    return <ProjectsSkeleton />;
  }

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-8 text-center text-destructive">
            Failed to load projects: {error.message}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (
    projects.length === 0 &&
    offerClients.length === 0 &&
    applications.length === 0 &&
    !applicationsPending
  ) {
    return <ProjectsEmptyState orgSlug={organization.slug} />;
  }

  const statusLabel = CAMPAIGN_STATUS_LABELS[status].toLowerCase();
  const selectedGroup = search.brand
    ? brandGroupsFor(status, tabItems, offerClients, coverImages).find(
        (group) => group.clientId === search.brand,
      )
    : undefined;

  if (search.brand && selectedGroup) {
    return (
      <BrandLogosProvider
        organizationId={organization.id}
        clientIds={logoClientIds}
      >
        <div className="p-4 sm:px-7 sm:py-8">
          <BrandCampaignsView
            key={`${status}-${selectedGroup.clientId}`}
            group={selectedGroup}
            items={tabItems.filter(
              (item) => item.project.client.id === selectedGroup.clientId,
            )}
            status={status}
            view={view}
            onViewChange={(next) =>
              updateSearch({ view: next === "list" ? "list" : undefined })
            }
            onBack={() => updateSearch({ brand: undefined, tab: status })}
            organizationId={organization.id}
            orgSlug={organization.slug}
          />
        </div>
      </BrandLogosProvider>
    );
  }

  const isEmpty =
    groupBy === "brand" ? groups.length === 0 : visibleItems.length === 0;

  return (
    <BrandLogosProvider
      organizationId={organization.id}
      clientIds={logoClientIds}
    >
      <div className="space-y-6 p-4 sm:px-7 sm:py-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-[30px]">
            Campaigns
          </h1>
          <p className="mt-2 text-[15px] text-muted-foreground">
            View campaigns and offers across all your creator profiles
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <StatusTabs
            status={status}
            counts={counts}
            onSelect={(next) =>
              updateSearch({
                tab: next,
                brand: undefined,
                tasks: next === "in-progress" ? tasks : undefined,
              })
            }
          />
          <div className="flex flex-wrap items-center gap-2">
            <ViewToggle
              view={view}
              onChange={(next) =>
                updateSearch({ view: next === "list" ? "list" : undefined })
              }
            />
            <GroupByMenu
              groupBy={groupBy}
              itemLabel="Campaign"
              onChange={(next) => {
                setGroupBy(next);
                if (search.brand) updateSearch({ brand: undefined });
              }}
            />
            {isInProgress && (
              <TaskFilter
                options={taskOptions}
                selected={tasks}
                onToggle={(taskType) =>
                  updateSearch({
                    tasks: tasks.includes(taskType)
                      ? tasks.filter((t) => t !== taskType)
                      : [...tasks, taskType],
                  })
                }
                onClear={() => updateSearch({ tasks: undefined })}
              />
            )}
          </div>
        </div>

        {search.brand && !selectedGroup ? (
          <CampaignsEmpty
            title="Brand not found"
            body="This brand doesn't exist or has no campaigns or offers."
          />
        ) : isEmpty ? (
          filterActive ? (
            <CampaignsEmpty
              title="No campaigns match this filter"
              body="Try a different task type or clear the filter."
              onClearFilter={() => updateSearch({ tasks: undefined })}
            />
          ) : (
            <CampaignsEmpty
              title={`No ${statusLabel} campaigns`}
              body="Campaigns will show here when they move to this status."
            />
          )
        ) : groupBy === "brand" ? (
          <div className="space-y-4">
            <BrandSummary
              brandCount={groups.length}
              campaignCount={visibleItems.length}
            />
            {view === "list" ? (
              <BrandTable groups={groups} orgSlug={organization.slug} />
            ) : (
              <BrandGrid groups={groups} orgSlug={organization.slug} />
            )}
          </div>
        ) : view === "list" ? (
          <CampaignTable
            items={visibleItems}
            status={status}
            orgSlug={organization.slug}
          />
        ) : (
          <div className={CAMPAIGN_GRID}>
            {visibleItems.map((item) => (
              <CampaignCard
                key={item.key}
                item={item}
                orgSlug={organization.slug}
              />
            ))}
          </div>
        )}
      </div>
    </BrandLogosProvider>
  );
}

// ============================================================================
// Skeleton / Empty State
// ============================================================================

function ProjectsSkeleton() {
  return (
    <div className="p-6 space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-96" />
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {SKELETON_DELAYS.map((delay, i) => (
          <Card
            // eslint-disable-next-line @eslint-react/no-array-index-key -- static skeleton items
            key={`wave-${i}`}
            className="overflow-hidden h-48"
            style={{
              animation: "grid-wave 1.2s ease-in-out infinite",
              animationDelay: `${delay}s`,
            }}
          >
            <div className="h-full w-full bg-muted" />
          </Card>
        ))}
      </div>
    </div>
  );
}

function ProjectsEmptyState({ orgSlug }: { orgSlug: string }) {
  return (
    <div className="p-6">
      <Card>
        <CardContent className="py-16 text-center">
          <FolderKanban className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h2 className="text-2xl font-semibold mb-2">No active campaigns</h2>
          <p className="text-muted-foreground max-w-md mx-auto mb-6">
            You haven't joined any campaigns yet. Explore the marketplace to
            find brands and opportunities that match your profile.
          </p>
          <Button asChild size="lg">
            <Link to="/o/$orgSlug/marketplace" params={{ orgSlug }}>
              <Search className="h-4 w-4 mr-2" />
              Browse Marketplace
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
